import { AltazionApiError, type ProblemDetails } from './errors.js'
import type { CommerceContext } from './CommerceContext.js'

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  headers?: Record<string, string>
  /** Nombre maximum de tentatives réseau (uniquement sur erreurs réseau, jamais sur 4xx) */
  maxRetries?: number
}

/**
 * Adaptateur HTTP basé sur fetch natif.
 *
 * Responsabilités :
 * - Construction des URLs à partir du baseUrl du contexte
 * - Ajout des headers standard (Content-Type, Accept, locale)
 * - credentials: 'include' pour la transmission automatique des cookies (modes browser et kiosk)
 * - Parse des réponses ProblemDetails en AltazionApiError
 * - Retry limité sur les erreurs réseau (TypeError), jamais sur les 4xx/5xx
 */
export class CommerceHttpAdapter {
  private readonly context: CommerceContext

  constructor(context: CommerceContext) {
    this.context = context
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { method = 'GET', body, headers = {}, maxRetries = 2 } = options
    const url = `${this.context.baseUrl}${path}`

    const requestHeaders: Record<string, string> = {
      Accept: 'application/json',
      'Accept-Language': this.context.locale,
      ...headers
    }

    if (body !== undefined) {
      requestHeaders['Content-Type'] = 'application/json'
    }

    const init: RequestInit = {
      method,
      headers: requestHeaders,
      credentials: 'include',
      body: body !== undefined ? JSON.stringify(body) : undefined
    }

    let lastNetworkError: unknown
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(url, init)
        return await this.handleResponse<T>(response)
      } catch (err) {
        // AltazionApiError n'est jamais retenté
        if (err instanceof AltazionApiError) throw err
        lastNetworkError = err
        // Petite pause exponentielle avant de réessayer (uniquement sur erreurs réseau)
        if (attempt < maxRetries) {
          await delay(150 * Math.pow(2, attempt))
        }
      }
    }

    throw lastNetworkError
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (response.ok) {
      const contentType = response.headers.get('content-type') ?? ''
      if (response.status === 204 || !contentType.includes('application/json')) {
        return undefined as unknown as T
      }
      return response.json() as Promise<T>
    }

    // Tenter de parser un ProblemDetails
    let problem: ProblemDetails = { status: response.status }
    try {
      const contentType = response.headers.get('content-type') ?? ''
      if (contentType.includes('application/json') || contentType.includes('application/problem+json')) {
        problem = await response.json() as ProblemDetails
        problem.status ??= response.status
      }
    } catch {
      // JSON invalide — on garde le problem minimal
    }

    throw new AltazionApiError(response.status, problem)
  }

  /** Effectue un GET et retourne T */
  get<T>(path: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(path, { method: 'GET', headers })
  }

  /** Effectue un POST avec un body JSON et retourne T */
  post<T>(path: string, body?: unknown, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(path, { method: 'POST', body, headers })
  }

  /** Effectue un PUT avec un body JSON et retourne T */
  put<T>(path: string, body?: unknown, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(path, { method: 'PUT', body, headers })
  }

  /** Effectue un PATCH avec un body JSON et retourne T */
  patch<T>(path: string, body?: unknown, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(path, { method: 'PATCH', body, headers })
  }

  /** Effectue un DELETE et retourne T */
  delete<T>(path: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(path, { method: 'DELETE', headers })
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
